import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useIsDesktopLayout, useIsMobileLayout } from '../hooks/useMediaQuery';

interface ResponsiveLayoutContextValue {
  isDesktop: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDrawerMode: boolean;
  isNavOpen: boolean;
  isAnalyticsNavOpen: boolean;
  showAnalyticsSubSidebar: boolean;
  toggleNav: () => void;
  toggleAnalyticsNav: () => void;
  closeAllNav: () => void;
}

const ResponsiveLayoutContext = createContext<ResponsiveLayoutContextValue | null>(null);

interface ResponsiveLayoutProviderProps {
  children: ReactNode;
  showAnalyticsSubSidebar?: boolean;
}

export function ResponsiveLayoutProvider({
  children,
  showAnalyticsSubSidebar = false,
}: Readonly<ResponsiveLayoutProviderProps>) {
  const location = useLocation();
  const isDesktop = useIsDesktopLayout();
  const isMobile = useIsMobileLayout();
  const isTablet = !isDesktop && !isMobile;
  const isDrawerMode = !isDesktop;

  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isAnalyticsNavOpen, setIsAnalyticsNavOpen] = useState(false);

  const closeAllNav = useCallback(() => {
    setIsNavOpen(false);
    setIsAnalyticsNavOpen(false);
  }, []);

  const toggleNav = useCallback(() => {
    setIsNavOpen(prev => {
      const next = !prev;
      if (next) {
        setIsAnalyticsNavOpen(false);
      }
      return next;
    });
  }, []);

  const toggleAnalyticsNav = useCallback(() => {
    setIsAnalyticsNavOpen(prev => {
      const next = !prev;
      if (next) {
        setIsNavOpen(false);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    closeAllNav();
  }, [location.pathname, closeAllNav]);

  useEffect(() => {
    if (isDesktop) {
      closeAllNav();
    }
  }, [isDesktop, closeAllNav]);

  useEffect(() => {
    const isOpen = isDrawerMode && (isNavOpen || isAnalyticsNavOpen);
    document.body.classList.toggle('ris-nav-open', isOpen);
    return () => document.body.classList.remove('ris-nav-open');
  }, [isDrawerMode, isNavOpen, isAnalyticsNavOpen]);

  const value = useMemo<ResponsiveLayoutContextValue>(
    () => ({
      isDesktop,
      isMobile,
      isTablet,
      isDrawerMode,
      isNavOpen,
      isAnalyticsNavOpen,
      showAnalyticsSubSidebar,
      toggleNav,
      toggleAnalyticsNav,
      closeAllNav,
    }),
    [
      isDesktop,
      isMobile,
      isTablet,
      isDrawerMode,
      isNavOpen,
      isAnalyticsNavOpen,
      showAnalyticsSubSidebar,
      toggleNav,
      toggleAnalyticsNav,
      closeAllNav,
    ]
  );

  return (
    <ResponsiveLayoutContext.Provider value={value}>{children}</ResponsiveLayoutContext.Provider>
  );
}

export function useResponsiveLayout(): ResponsiveLayoutContextValue {
  const context = useContext(ResponsiveLayoutContext);
  if (!context) {
    throw new Error('useResponsiveLayout must be used within ResponsiveLayoutProvider');
  }
  return context;
}

export function useOptionalResponsiveLayout(): ResponsiveLayoutContextValue | null {
  return useContext(ResponsiveLayoutContext);
}
